/**
 * Client-Side Canvas-Renderer fuer Fehler-Analyse-Snapshots aus der
 * SynmonAnalyse. Kombiniert Screenshot (Base64-Data-URI) + Meta-Info
 * in ein einzelnes Bild und liefert es entweder als downloadbares JPG
 * oder als PNG-Blob fuer die Zwischenablage.
 *
 * Wichtig: reine Web-Native-APIs, keine externe Dependency.
 * Beide Aufrufe funktionieren offline (kein Netzwerk-Roundtrip).
 */

// ---------------------------------------------------------------------------
// Layout-Konstanten (in Canvas-Pixeln bei devicePixelRatio 1)
// ---------------------------------------------------------------------------
const PADDING            = 32;
const CANVAS_WIDTH_MIN   = 1200;   // Minimum-Breite (Screenshot ist meist breiter, dann skalieren wir Meta-Bereich)
const HEADER_BAR_HEIGHT  = 8;      // Freenet-gruener Akzent-Streifen
const HEADER_LOGO_Y      = 24;
const TITLE_FONT_SIZE    = 26;
const SUBTITLE_FONT_SIZE = 16;
const META_LABEL_WIDTH   = 200;    // Breite der "Label:"-Spalte im Meta-Block
const META_ROW_GAP       = 12;
const META_FONT_SIZE     = 18;
const META_LINE_HEIGHT   = 26;
const DIVIDER_MARGIN     = 20;
const SECTION_GAP        = 28;
const SCREENSHOT_MAX_WIDTH = 2400; // absolute Obergrenze (uebergrosse Screenshots werden skaliert)

// Facelift-Farben (hardcoded, weil Canvas keine CSS-Variablen aufloest)
const COLOR_BG            = '#ffffff';
const COLOR_HEADER_BG     = '#faf9f7';   // warmes Off-White (Light-Mode Widget-BG)
const COLOR_ACCENT        = '#84bc34';   // Freenet-Gruen (brand-500)
const COLOR_TEXT          = '#17171b';
const COLOR_SUBTEXT       = '#55555e';
const COLOR_META_BG       = '#f5f4f1';   // sehr dezenter Meta-Block-Hintergrund
const COLOR_DIVIDER       = 'rgba(23,23,27,0.08)';
const COLOR_ERROR_LABEL   = '#c1381d';   // fuer "Fehler-Kurzinfo"-Label als Signal

// Font-Stack mit Mac-Fallback (Segoe UI ist Windows-only; -apple-system /
// BlinkMacSystemFont / SF Pro Display sichern Mac + Safari-Kompatibilitaet).
const FONT_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif';

// ---------------------------------------------------------------------------
// wrapText — bricht Text an Wort-Grenzen um, damit lange Fehler-Texte
// vollstaendig sichtbar bleiben. Rueckgabe: Array von Zeilen.
// ---------------------------------------------------------------------------
function wrapText(ctx, text, maxWidth) {
    if (!text) return [''];
    const paragraphs = String(text).split(/\r?\n/);
    const lines = [];
    for (const para of paragraphs) {
        if (!para.length) { lines.push(''); continue; }
        const words = para.split(/\s+/);
        let current = '';
        for (const word of words) {
            const test = current ? current + ' ' + word : word;
            if (ctx.measureText(test).width > maxWidth && current) {
                lines.push(current);
                current = word;
            } else {
                current = test;
            }
        }
        if (current) lines.push(current);
    }
    return lines;
}

// ---------------------------------------------------------------------------
// loadImage — Data-URI oder URL in HTMLImageElement laden
// ---------------------------------------------------------------------------
function loadImage(src) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = src;
    });
}

// ---------------------------------------------------------------------------
// buildSnapshotCanvas — Kern-Renderer. Baut das Canvas aus den Meta-Daten
// und dem Screenshot auf und gibt es zurueck.
//
// meta = {
//   productName, testName, testID, productID,
//   errorTimestamp, errorShort, exportedAt
// }
// screenshotSrc = Base64-Data-URI oder gleich-origin URL
// ---------------------------------------------------------------------------
async function buildSnapshotCanvas(meta, screenshotSrc) {
    // 1) Screenshot vorab laden — brauchen echte Dimensionen fuer Canvas-Setup
    const img = await loadImage(screenshotSrc);

    // 2) Canvas-Breite bestimmen: Screenshot-Breite, aber mindestens Meta-Min,
    //    maximal SCREENSHOT_MAX_WIDTH (fuer sehr grosse Screenshots skalieren).
    let screenshotDrawWidth  = img.naturalWidth;
    let screenshotDrawHeight = img.naturalHeight;
    if (screenshotDrawWidth > SCREENSHOT_MAX_WIDTH) {
        const scale = SCREENSHOT_MAX_WIDTH / screenshotDrawWidth;
        screenshotDrawWidth  = SCREENSHOT_MAX_WIDTH;
        screenshotDrawHeight = Math.round(img.naturalHeight * scale);
    }
    const contentWidth = Math.max(CANVAS_WIDTH_MIN, screenshotDrawWidth);
    const canvasWidth  = contentWidth + PADDING * 2;

    // 3) Meta-Textblock messen (Auto-Wrap fuer errorShort — kann beliebig lang sein)
    const measureCanvas = document.createElement('canvas');
    const measureCtx    = measureCanvas.getContext('2d');
    measureCtx.font     = `${META_FONT_SIZE}px ${FONT_STACK}`;

    const metaValueMaxWidth = contentWidth - META_LABEL_WIDTH - PADDING;

    const metaRows = [
        { label: 'Produkt',            value: meta.productName || '—' },
        { label: 'Test',               value: meta.testName || '—' },
        { label: 'Produkt-ID',         value: meta.productID || '—' },
        { label: 'Test-ID',            value: meta.testID || '—' },
        { label: 'Fehler-Zeitpunkt',   value: meta.errorTimestamp || '—' },
        { label: 'Exportiert am',      value: meta.exportedAt || '—' },
        { label: 'Fehler-Kurzinfo',    value: meta.errorShort || '—', highlight: true, wrap: true },
    ];

    // Pro Row: Zeilen berechnen (nur die "wrap"-Row wird umgebrochen, Rest ist einzeilig)
    let metaBlockHeight = 0;
    for (const row of metaRows) {
        if (row.wrap) {
            row.wrappedLines = wrapText(measureCtx, row.value, metaValueMaxWidth);
            row.height = Math.max(META_LINE_HEIGHT, row.wrappedLines.length * META_LINE_HEIGHT);
        } else {
            row.wrappedLines = [row.value];
            row.height = META_LINE_HEIGHT;
        }
        metaBlockHeight += row.height + META_ROW_GAP;
    }
    metaBlockHeight += PADDING * 2; // internes Padding der Meta-Card

    // 4) Header-Block-Hoehe (fix: brand-Bar + Titel + Subtitle + Padding)
    const headerBlockHeight = HEADER_BAR_HEIGHT + PADDING + TITLE_FONT_SIZE + 10
                            + SUBTITLE_FONT_SIZE + PADDING;

    // 5) Gesamte Canvas-Hoehe berechnen
    const canvasHeight = headerBlockHeight
                       + SECTION_GAP + metaBlockHeight
                       + SECTION_GAP + screenshotDrawHeight
                       + PADDING;

    // 6) Ziel-Canvas erstellen (nur im Speicher, kein DOM-Append)
    const canvas = document.createElement('canvas');
    canvas.width  = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');
    ctx.textBaseline = 'top';

    // -------- BG
    ctx.fillStyle = COLOR_BG;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // -------- Header
    let y = 0;
    ctx.fillStyle = COLOR_HEADER_BG;
    ctx.fillRect(0, 0, canvasWidth, headerBlockHeight);
    ctx.fillStyle = COLOR_ACCENT;
    ctx.fillRect(0, 0, canvasWidth, HEADER_BAR_HEIGHT);

    y = HEADER_LOGO_Y + PADDING / 2;
    ctx.fillStyle = COLOR_TEXT;
    ctx.font = `700 ${TITLE_FONT_SIZE}px ${FONT_STACK}`;
    ctx.fillText('Fehleranalyse-Snapshot', PADDING, y);
    y += TITLE_FONT_SIZE + 6;

    ctx.fillStyle = COLOR_SUBTEXT;
    ctx.font = `500 ${SUBTITLE_FONT_SIZE}px ${FONT_STACK}`;
    const subtitle = `${meta.productName || '—'}  ·  ${meta.testName || '—'}`;
    ctx.fillText(subtitle, PADDING, y);

    // -------- Meta-Card
    y = headerBlockHeight + SECTION_GAP;
    ctx.fillStyle = COLOR_META_BG;
    ctx.fillRect(PADDING, y, contentWidth, metaBlockHeight);

    // Meta-Rows zeichnen
    let rowY = y + PADDING;
    for (const row of metaRows) {
        // Label
        ctx.font = `600 ${META_FONT_SIZE}px ${FONT_STACK}`;
        ctx.fillStyle = row.highlight ? COLOR_ERROR_LABEL : COLOR_SUBTEXT;
        ctx.fillText(row.label, PADDING * 1.5, rowY);

        // Value(s)
        ctx.font = `400 ${META_FONT_SIZE}px ${FONT_STACK}`;
        ctx.fillStyle = COLOR_TEXT;
        for (let i = 0; i < row.wrappedLines.length; i++) {
            ctx.fillText(row.wrappedLines[i], PADDING * 1.5 + META_LABEL_WIDTH, rowY + i * META_LINE_HEIGHT);
        }

        // Divider zwischen Rows (nicht nach letztem)
        if (row !== metaRows[metaRows.length - 1]) {
            const dividerY = rowY + row.height + META_ROW_GAP / 2 - 4;
            ctx.fillStyle = COLOR_DIVIDER;
            ctx.fillRect(PADDING * 1.5, dividerY, contentWidth - PADDING, 1);
        }

        rowY += row.height + META_ROW_GAP;
    }

    // -------- Divider zwischen Meta und Screenshot
    y = y + metaBlockHeight + SECTION_GAP / 2;
    ctx.fillStyle = COLOR_DIVIDER;
    ctx.fillRect(PADDING, y, contentWidth, 1);

    // -------- Screenshot
    y = y + SECTION_GAP / 2;
    const screenshotX = PADDING + Math.max(0, (contentWidth - screenshotDrawWidth) / 2);
    ctx.drawImage(img, screenshotX, y, screenshotDrawWidth, screenshotDrawHeight);

    return canvas;
}

// ---------------------------------------------------------------------------
// Public: Download als JPG (browser-Download-Dialog)
// ---------------------------------------------------------------------------
export async function downloadErrorSnapshotJPG(meta, screenshotSrc, filename) {
    const canvas = await buildSnapshotCanvas(meta, screenshotSrc);
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) { reject(new Error('Blob-Erzeugung fehlgeschlagen')); return; }
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename || 'fehleranalyse-snapshot.jpg';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                // ObjectURL nach kurzer Delay wieder freigeben
                setTimeout(() => URL.revokeObjectURL(url), 1000);
                resolve();
            },
            'image/jpeg',
            0.92
        );
    });
}

// ---------------------------------------------------------------------------
// Public: In Zwischenablage kopieren (als PNG, weil Clipboard-API image/jpeg
// nicht garantiert unterstuetzt)
//
// Safari-kompatibel: nutzt das "Lazy-Blob"-Pattern (Promise-Value im
// ClipboardItem). Der navigator.clipboard.write()-Call wird SYNCHRON im
// User-Gesture-Kontext ausgefuehrt, waehrend die eigentliche Blob-Berechnung
// asynchron im Hintergrund laeuft. Ohne dieses Pattern verliert Safari nach
// mehrfachem await den Gesture-Kontext und wirft NotAllowedError.
// ---------------------------------------------------------------------------
export async function copyErrorSnapshotToClipboard(meta, screenshotSrc) {
    if (!navigator.clipboard || typeof ClipboardItem === 'undefined') {
        throw new Error('Zwischenablage-API in diesem Browser nicht verfuegbar (bitte HTTPS + moderner Browser).');
    }

    // Promise, die den PNG-Blob spaeter aufloest. Wichtig: NICHT vorher
    // awaiten — wir uebergeben die Promise als Value ins ClipboardItem.
    const pngBlobPromise = (async () => {
        const canvas = await buildSnapshotCanvas(meta, screenshotSrc);
        return new Promise((resolve, reject) => {
            canvas.toBlob(
                (blob) => blob ? resolve(blob) : reject(new Error('Blob-Erzeugung fehlgeschlagen')),
                'image/png'
            );
        });
    })();

    // Clipboard-Write direkt (synchron im User-Gesture-Kontext).
    // Safari akzeptiert Promise-Values in ClipboardItem seit Safari 15.4+.
    // Aeltere Safari-Versionen fallen mit einem Fehler zurueck — der wird
    // vom Aufrufer als Notify angezeigt.
    await navigator.clipboard.write([
        new ClipboardItem({ 'image/png': pngBlobPromise })
    ]);
}
